// Service worker registration, the update prompt, and Web Push.
//
// Ported from the Andon app's `pwa.ts`. The shop floor registered no worker
// before the merge — the config generated one and nothing ever called
// `registerSW` — so this is where the app gains a precached shell, an offline
// front door and, with the andon preview on, pushes.
//
// Push is what makes an andon reach a responder who is walking the floor with
// their phone in a pocket. It is never the only channel — everything pushed is
// also an Odoo activity and a chatter entry — so a declined permission
// degrades to a slower notification rather than a missed one.
//
// The subscription is registered against the person signed in: an Odoo user,
// or the employee behind a PIN. A device with nobody confirmed on it has
// nobody to address, and the server says so.

import { registerSW } from 'virtual:pwa-register';
import { alertIdOf } from '@/sw/handlers';
import { classifyPush, clearPushSnooze, readPushEnv, setPushHealth, type PushHealth } from '@/lib/pushHealth';

let updateSW: ((reload?: boolean) => Promise<void>) | null = null;

/**
 * The live registration, kept so a person can ask about updates on their own
 * account. The automatic checks below are deliberately quiet — they only ever
 * light the banner up — and that leaves nowhere to stand when someone has been
 * told a fix went out and wants to know whether this device has it.
 */
let swRegistration: ServiceWorkerRegistration | null = null;

/**
 * How often to go and ask whether there is a newer build. Half an hour is a
 * request the size of one script tag against a deploy cadence measured in days
 * — the cost is nothing and the failure it prevents is a floor running last
 * week's app.
 */
const UPDATE_CHECK_INTERVAL_MS = 30 * 60_000;

/** Floor on how often a check may actually fire, whatever triggers it. */
const UPDATE_CHECK_FLOOR_MS = 60_000;

/**
 * Register the worker. Updates are applied when somebody says yes rather than
 * immediately: swapping the bundle out from under someone mid-pod is the one
 * moment this app must not blink.
 *
 * "When somebody says yes" needs somebody to be ASKED, though. A tablet in
 * standalone mode is never loaded again — nobody closes every tab of a kiosk
 * — so a waiting worker waits for days and the device keeps running the
 * bundle it had. Hence onWaiting: something has to SAY an update is ready, or
 * the choice to apply it never reaches anybody.
 *
 * NOBODY ASKS, SO NOTHING IS EVER FOUND
 * -------------------------------------
 * onWaiting only fires once the browser has DISCOVERED the new worker, and the
 * browser only looks on a navigation (plus a roughly daily check of its own).
 * On a desktop tab that is constant — every reload is a look. An installed PWA
 * is launched once and then lives in the app switcher for a week: no
 * navigation, no look, no discovery, and the update button simply never has
 * anything to announce.
 *
 * So ask on our own account, on the two occasions that matter:
 *
 *   on a timer          a wall tablet nobody touches still catches up
 *   on becoming visible the moment a phone comes out of a pocket, which is the
 *                       only reliable "the app just started being used again"
 *                       signal a standalone PWA gets
 *
 * Both funnel through one throttled call, because visibilitychange fires on
 * every glance at the notification shade and this is a network request.
 */
export function registerServiceWorker(onWaiting?: () => void): void {
  if (!('serviceWorker' in navigator)) return;
  updateSW = registerSW({
    immediate: false,
    onNeedRefresh: () => onWaiting?.(),
    onRegisteredSW: (_swUrl, registration) => {
      if (!registration) return;
      swRegistration = registration;

      // A worker can already BE waiting the moment we register — a second tab
      // downloaded it, or the last visit ended before anyone said yes. That is
      // an update nobody was told about, and it is not an update event, so
      // onNeedRefresh will not mention it. `controller` is the check that this
      // is a replacement rather than the very first install of the app.
      if (registration.waiting && navigator.serviceWorker.controller) onWaiting?.();

      let lastCheck = Date.now();
      const check = () => {
        // Hidden means nobody is looking, and offline means the request can
        // only fail. Neither is worth waking the radio for.
        if (document.visibilityState !== 'visible' || navigator.onLine === false) return;
        const now = Date.now();
        if (now - lastCheck < UPDATE_CHECK_FLOOR_MS) return;
        lastCheck = now;
        // A failed check is not worth reporting: it means the server was
        // unreachable, which the rest of the app is already shouting about.
        void registration.update().catch(() => {});
      };

      // Deliberately never cleared. This lives for as long as the document
      // does, which on the devices this exists for is measured in days.
      setInterval(check, UPDATE_CHECK_INTERVAL_MS);
      document.addEventListener('visibilitychange', check);
    },
  });
}

export type UpdateCheckResult = 'update-ready' | 'up-to-date' | 'unsupported' | 'failed';

/**
 * Ask the server, right now, whether there is a newer build.
 *
 * Unthrottled on purpose: the floor between the automatic checks exists so a
 * phone coming out of a pocket forty times an hour does not make forty
 * requests. A person deliberately pressing a button is the one caller whose
 * request is always worth making.
 *
 * `update()` resolves once the browser has fetched and, if the bytes differ,
 * INSTALLED the new worker — but `waiting` is only populated at the end of
 * that install, so a worker still in `installing` when the promise settles has
 * to be waited out or a genuine update reports as up-to-date.
 */
export async function checkForUpdate(): Promise<UpdateCheckResult> {
  const registration = swRegistration;
  if (!registration) return 'unsupported';
  try {
    await registration.update();
  } catch {
    // Almost always the server being unreachable. Distinguished from
    // up-to-date because "we could not ask" and "we asked and you are current"
    // are different answers to the person standing there.
    return 'failed';
  }
  const installing = registration.installing;
  if (installing) {
    await new Promise<void>((resolve) => {
      installing.addEventListener('statechange', () => {
        // 'installed' means it is now waiting; 'redundant' means it lost to
        // another tab's worker or failed. Either way the question is settled.
        if (installing.state === 'installed' || installing.state === 'redundant') resolve();
      });
    });
  }
  // `controller` again: on a first install there is a waiting worker and no
  // update — that is the app arriving, not a new version of it.
  return registration.waiting && navigator.serviceWorker.controller ? 'update-ready' : 'up-to-date';
}

/** Activate the waiting worker and reload onto it. */
export function applyPendingUpdate(): void {
  void updateSW?.(true);
}

/**
 * Listen for a tapped notification asking the open tab to show an andon.
 *
 * The worker posts {type:'open-alert'} to a window it found open; with no
 * window it opens /alert/<id> instead, which `lib/route` lands on. Same
 * destination either way, so the two ways in cannot drift.
 *
 * Positive integers only, which is the rule every alert id in the app
 * follows: the test notification is a push whose id is deliberately 0, and
 * tapping it must open nothing rather than an empty sheet.
 */
export function listenForOpenAlert(onOpen: (alertId: number) => void): void {
  navigator.serviceWorker?.addEventListener('message', (event: MessageEvent) => {
    const data = event.data as { type?: string; alertId?: unknown } | undefined;
    if (data?.type !== 'open-alert') return;
    const id = alertIdOf(data.alertId);
    if (id != null) onOpen(id);
  });
}

/** VAPID keys travel as base64url; PushManager wants raw bytes. */
function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normalized);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

/**
 * Get this device subscribed, and say where it got to.
 *
 * Every exit names itself — see `lib/pushHealth` for why a bare `return` on
 * each of these was the whole reported symptom.
 *
 * `prompt` IS THE OTHER HALF OF THE FIX, AND IT IS AN iOS BUG.
 * -----------------------------------------------------------
 * The Andon app used to call `Notification.requestPermission()` on its own
 * account, during session restore. On iOS that is a permission request with
 * no user gesture behind it, and WebKit rejects those outright — so on the
 * exact platform where push is hardest to get working, the dialog never
 * appeared and the app recorded a refusal nobody was ever offered. Chromium
 * allows it, but repays an unprompted request with its quieter permission UI
 * and, often enough, a permanent block that no amount of later asking can undo.
 *
 * So the boot path never prompts. It reports `ask`, the banner shows, and the
 * request is made from the tap on its button — a real gesture, on every
 * platform, at a moment the person has just been told what it is for.
 */
async function syncPush(prompt: boolean): Promise<PushHealth> {
  const settle = (registered: boolean, serverPush = true): PushHealth => {
    const health = classifyPush(readPushEnv(serverPush, registered));
    setPushHealth(health);
    // A device that has come good must not stay quiet for the rest of a
    // week-long dismissal it earned while it was broken — and, more to the
    // point, must be free to speak up the moment it breaks again.
    if (health === 'ok') clearPushSnooze();
    return health;
  };

  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    return settle(false);
  }

  // Loaded here rather than at the top so the worker registration above can
  // run before the API client — and everything it imports — is evaluated.
  const { andonApi } = await import('@/api/andon/client');
  const { ApiError } = await import('@/api/http');
  // A failure here is indistinguishable from a deployment with no VAPID keys,
  // and both mean the same thing to the person holding the phone: there is
  // nothing to subscribe to yet. `off` says nothing rather than guessing.
  // A dormant andon module answers 404 here, which lands in the same place.
  const { publicKey } = await andonApi.notifications().catch(() => ({ publicKey: null }));
  if (!publicKey) return settle(false, false);

  /**
   * Hand the subscription to the server and say whether it took.
   *
   * `operator_required` is a refusal rather than a failure: the server has no
   * employee to register the device against — a kiosk waiting on a PIN, or an
   * Odoo account with no `hr.employee` record. Reporting that as `unregistered`
   * would offer a Retry button that can only fail again, so it settles to `off`
   * and the app says nothing until there is somebody to address.
   */
  const offer = async (json: PushSubscriptionJSON): Promise<'ok' | 'failed' | 'no-subject'> => {
    if (!json.endpoint || !json.keys?.['p256dh'] || !json.keys?.['auth']) return 'failed';
    try {
      await andonApi.subscribePush({
        endpoint: json.endpoint,
        keys: { p256dh: json.keys['p256dh'], auth: json.keys['auth'] },
      });
      return 'ok';
    } catch (err) {
      return err instanceof ApiError && err.code === 'operator_required' ? 'no-subject' : 'failed';
    }
  };

  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();
  if (existing) {
    // Re-register on every boot: the server holds subscriptions in memory, so
    // a restart forgets them and this is what puts them back. Whether the
    // server ACCEPTED it is what decides `registered`: a subscription the
    // browser holds and the server has never heard of is a phone that will
    // not buzz, and it looks identical to a healthy one otherwise.
    const outcome = await offer(existing.toJSON());
    if (outcome === 'no-subject') return settle(false, false);
    return settle(outcome === 'ok');
  }

  if (Notification.permission === 'denied') return settle(false);
  // Not asked yet, and nobody has tapped anything. Say so and stop: see above.
  if (Notification.permission !== 'granted' && !prompt) return settle(false);

  if (Notification.permission !== 'granted') {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return settle(false);
  }

  try {
    const sub = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
    });
    const outcome = await offer(sub.toJSON());
    if (outcome === 'no-subject') return settle(false, false);
    return settle(outcome === 'ok');
  } catch {
    // Refused by the browser or by the push service. The in-app list still
    // carries the alert, which is what `unregistered` is telling somebody.
    return settle(false);
  }
}

/**
 * Boot path: re-register a subscription that already exists, and work out what
 * to say when there is not one. Never opens a permission dialog.
 *
 * Called once there is somebody signed in, and only with the andon preview on.
 */
export function registerPush(): Promise<PushHealth> {
  return syncPush(false);
}

/**
 * The button. Prompts, subscribes, and reports.
 *
 * Must be called from a real user gesture — that is the entire reason it is
 * separate from `registerPush`.
 */
export function enablePush(): Promise<PushHealth> {
  return syncPush(true);
}

/** Re-read health without touching anything. For a screen coming back into view. */
export function refreshPushHealth(): Promise<PushHealth> {
  return syncPush(false);
}

/**
 * Floor on how often health may be re-checked from a visibility change, which
 * fires on every glance at the notification shade and costs a request.
 */
const HEALTH_RECHECK_FLOOR_MS = 60_000;

/**
 * Watch for push health changing outside the app, and keep the reading honest.
 *
 * There is no event for `Notification.permission`. Somebody told where the
 * switch is goes to Settings, turns notifications on, and comes back to an app
 * that still insists they are blocked — which is the app being caught wrong
 * about the one thing it was nagging about.
 *
 * Two signals, because neither is enough on its own:
 *
 *   permissions.onchange  exact, immediate, and Chromium-only. Safari has
 *                         `navigator.permissions` but has never answered for
 *                         'notifications', so the query throws and is caught.
 *   visibilitychange      the fallback, and the only one iOS gets. It is also
 *                         the only reliable "this app just started being used
 *                         again" signal a standalone PWA has — a phone coming
 *                         out of a pocket — so it doubles as the moment to
 *                         re-register a subscription the server may have
 *                         forgotten while the app sat in the app switcher.
 *
 * Returns its own teardown, so it can be handed straight to an effect.
 */
export function watchPushHealth(): () => void {
  let last = Date.now();
  const check = () => {
    if (document.visibilityState !== 'visible' || navigator.onLine === false) return;
    const now = Date.now();
    if (now - last < HEALTH_RECHECK_FLOOR_MS) return;
    last = now;
    void syncPush(false);
  };

  document.addEventListener('visibilitychange', check);

  let status: PermissionStatus | null = null;
  const onChange = () => {
    // A permission that just changed is worth acting on immediately, whatever
    // the throttle says — this fires once, on a real change, not on a glance.
    last = 0;
    check();
  };
  navigator.permissions
    ?.query({ name: 'notifications' as PermissionName })
    .then((s) => {
      status = s;
      s.addEventListener('change', onChange);
    })
    .catch(() => {
      // Safari, or a browser that does not answer for this permission. The
      // visibility check above is the whole of the coverage there.
    });

  return () => {
    document.removeEventListener('visibilitychange', check);
    status?.removeEventListener('change', onChange);
  };
}

/**
 * Unbind this device from the person, but keep the browser subscription.
 *
 * For an operator signing out of a device that stays in service. The operator
 * goes and the DEVICE stays, so throwing away the PushManager subscription
 * would be wrong twice over: the next operator would wait on a fresh
 * subscribe, and where the permission was granted long ago there is nothing
 * to re-grant — it is pure delay for no gain.
 *
 * What must not survive is the BINDING. Between one person leaving and the
 * next signing in, an alert for the person who has gone home would otherwise
 * buzz on a device somebody else is holding. The server also moves an
 * endpoint to whoever subscribes it next, so this is the belt to that braces
 * — it closes the window between the two sign-ins rather than the handover
 * itself.
 */
export async function detachPush(): Promise<void> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
  const registration = await navigator.serviceWorker.getRegistration();
  const sub = await registration?.pushManager.getSubscription();
  if (!sub) return;
  const { andonApi } = await import('@/api/andon/client');
  await andonApi.unsubscribePush(sub.endpoint).catch(() => {});
  setPushHealth('off');
}

/**
 * Drop this device's push subscription entirely. Called on a full sign-out, and
 * only then: a phone that keeps its subscription after its owner signs out keeps
 * buzzing for alerts they can no longer open — and on a shared phone it would
 * buzz for the wrong person entirely.
 *
 * The server is told first, because it needs the session that is about to be
 * destroyed in order to know whose subscription this is.
 */
export async function unregisterPush(): Promise<void> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
  const registration = await navigator.serviceWorker.getRegistration();
  const sub = await registration?.pushManager.getSubscription();
  if (!sub) return;
  const { andonApi } = await import('@/api/andon/client');
  await andonApi.unsubscribePush(sub.endpoint).catch(() => {});
  await sub.unsubscribe().catch(() => {});
  // Back to nothing to say. The next sign-in re-runs the whole check, and a
  // stale `ok` on the sign-in screen would be a claim about a device that is no
  // longer subscribed to anything.
  setPushHealth('off');
}
